import * as Sentry from "@sentry/react";
import {
  Box,
  Button,
  Code,
  Heading,
  Stack,
  Text,
} from "@chakra-ui/react";

/**
 * Top-level crash guard. Until this existed, any uncaught throw in a render or an effect
 * unmounted the whole React tree and left a blank white page with nothing on screen - and
 * because Sentry reporting is off in dev (VITE_SENTRY_ENABLED, see main.tsx), the only trace
 * was a console.error you'd miss entirely unless devtools happened to be open at the moment
 * it threw. This turns that silent white page into a readable message.
 *
 * Sentry.ErrorBoundary is used rather than a hand-rolled componentDidCatch class purely
 * because @sentry/react is already a dependency: the boundary itself works with reporting
 * disabled, and when VITE_SENTRY_ENABLED is "true" the same throw is also reported upstream
 * with no extra wiring here.
 *
 * Mounted inside ChakraProvider (Router.tsx) so the fallback below has the theme in scope,
 * and outside everything else so provider-level failures are caught too.
 */

function ErrorFallback({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) {
  const err = error instanceof Error ? error : undefined;
  const message = err?.message ?? String(error);

  return (
    <Box maxW="3xl" mx="auto" p={8}>
      <Stack spacing={4} align="flex-start">
        <Heading size="md">Something went wrong on this page</Heading>
        <Text fontSize="sm" color="gray.600">
          The page stopped rather than showing you a half-broken screen. Nothing you had
          already saved is affected. Try again, and if it keeps happening send the detail
          below to the dev team.
        </Text>
        <Code
          p={3}
          w="100%"
          whiteSpace="pre-wrap"
          fontSize="xs"
          colorScheme="red"
        >
          {message}
        </Code>
        {/* Stack only in dev - in production it's noise to the user, and the same trace is in
            Sentry anyway. */}
        {import.meta.env.DEV && err?.stack && (
          <Code p={3} w="100%" whiteSpace="pre-wrap" fontSize="xs" maxH="20rem" overflowY="auto">
            {err.stack}
          </Code>
        )}
        <Stack direction="row" spacing={3}>
          {/* resetError re-mounts the subtree without a full page load, so it recovers from a
              transient throw while keeping auth state and any loaded caches. */}
          <Button size="sm" onClick={resetError}>
            Try again
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function AppErrorBoundary({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
